<template>
  <div
    v-if="(!!order.deliveryType && order.deliveryType !== DELIVERY_TYPE_OPTIONS.DIRECT_TO_CUSTOMER && order.deliveryType !== DELIVERY_TYPE_OPTIONS.VIRTUAL) || order.anonymous"
    id="order-details"
    class="q-mx-xs"
    :class="$register && $q.screen.xs ? 'full-width' : undefined"
  >
    <component
      :is="$q.screen.gt.xs ? 'h2' : 'h6'"
      v-if="$register"
      class="q-mt-sm q-mb-xs"
    >
      {{ $t('pages.payment.deliveryHeading') }}
    </component>

    <delivery-information-list
      :order="order"
    >
      <template #title>
        <q-item-section :class="$q.screen.gt.xs ? 'text-h6' : 'text-weight-bolder text-body1'">
          {{ $t('pages.payment.foodAndDrinkHeading') }}
        </q-item-section>
      </template>
    </delivery-information-list>

    <q-list
      v-if="showContactDetailsHeader"
      :class="{ 'bg-responsive q-mt-sm': !$register }"
    >
      <q-item
        class="bg-responsive-grey-3"
      >
        <q-item-section avatar>
          <q-avatar
            icon="fal fa-at"
            text-color="dark"
          />
        </q-item-section>

        <q-item-section :class="$q.screen.gt.xs ? 'text-h6' : 'text-weight-bolder text-body1'">
          {{ $t('pages.payment.contactDetailsHeading') }}
        </q-item-section>
      </q-item>
    </q-list>
    <div
      v-if="order.anonymous"
      class="row q-col-gutter-x-sm"
      :class=" $register ? undefined : 'bg-responsive q-pr-sm q-pt-sm q-ml-none'"
    >
      <q-input
        v-if="collectEmail"
        ref="deliveryEmailAddress"
        v-model="order.deliveryEmailAddress"
        class="col-12"
        :class="{ 'bg-responsive': !$register }"
        type="email"
        :label="$t('form.labels.emailAddress')"
        bottom-slots
        :rules="[
          val => !emailRequired || required.$validator(val) || $t('form.errors.required'),
          val => !emailRequired || email.$validator(val.toLowerCase()) || $t('form.errors.emailNotValid')
        ]"
        lazy-rules
        :debounce="300"
        data-test-id="payments-form-email-address"
        @update:model-value="validateDeliveryEmailAddress"
      />
      <template v-if="collectName">
        <q-input
          ref="deliveryFirstName"
          v-model="order.deliveryFirstName"
          class="col-6"
          :class="{ 'bg-responsive': !$register }"
          :label="$t('form.labels.firstName') + (nameRequired ? '' : ` (${$t('common.optional')})`)"
          bottom-slots
          :rules="[val => !nameRequired || required.$validator(val) || $t('form.errors.required'),]"
          lazy-rules
          data-test-id="payments-form-first-name"
        />
        <q-input
          ref="deliveryLastName"
          v-model="order.deliveryLastName"
          class="col-6"
          :class="{ 'bg-responsive': !$register }"
          :label="$t('form.labels.lastName') + (nameRequired ? '' : ` (${$t('common.optional')})`)"
          bottom-slots
          :rules="[val => !nameRequired || required.$validator(val) || $t('form.errors.required'),]"
          lazy-rules
          data-test-id="payments-form-last-name"
        />
      </template>
    </div>
    <phone-number-input
      v-if="collectPhone"
      ref="deliveryPhone"
      :phone="order.deliveryPhone"
      class="col-12 q-mx-sm q-my-sm"
      :required="phoneRequired"
      @phone-changed="handlePhoneChange"
    />
  </div>
</template>

<script>
import { email, required } from '@vuelidate/validators'
import { DELIVERY_TYPE_OPTIONS } from 'src/models/delivery-type/DeliveryType'
import User from 'src/models/user/User'
import PhoneNumberInput from 'src/modules/consumer/components/phone/PhoneNumberInput'
import DeliveryInformationList from 'src/modules/register/payment/components/DeliveryInformationList'

export default {
  name: 'OrderContactDetails',
  components: {
    DeliveryInformationList,
    PhoneNumberInput
  },
  configuration: ['currentSite'],
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  emits: ['showMarketingEmailsOptIn', 'receiveMarketingEmails', 'isValidated'],
  computed: {
    collectEmail () {
      return this.$consumer || this.$kiosk || (this.$register && this.currentSite.anonymousOrderRequirements.phone_prompt_for_email)
    },
    emailRequired () {
      return this.$consumer || this.$kiosk || (this.$register && this.currentSite.anonymousOrderRequirements.phone_email_required)
    },
    collectName () {
      return (
        ((this.$consumer || this.$kiosk) && this.currentSite.anonymousOrderRequirements.consumer_prompt_for_name) ||
        (this.$register && this.currentSite.anonymousOrderRequirements.phone_prompt_for_name)
      )
    },
    nameRequired () {
      return (
        ((this.$consumer || this.$kiosk) && this.currentSite.anonymousOrderRequirements.consumer_name_required) ||
        (this.$register && this.currentSite.anonymousOrderRequirements.phone_name_required)
      )
    },
    collectPhone () {
      return (
        ((this.$consumer || this.$kiosk) && this.currentSite.anonymousOrderRequirements.consumer_prompt_for_phone) ||
        (this.$register && this.currentSite.anonymousOrderRequirements.phone_prompt_for_phone)
      )
    },
    phoneRequired () {
      return (
        ((this.$consumer || this.$kiosk) && this.currentSite.anonymousOrderRequirements.consumer_phone_required) ||
        (this.$register && this.currentSite.anonymousOrderRequirements.phone_phone_required)
      )
    },
    showContactDetailsHeader () {
      return (this.order.anonymous && !this.$register) ||
      (this.$consumer && this.currentSite.anonymousOrderRequirements.consumer_prompt_for_phone) ||
      (this.$register && this.currentSite.anonymousOrderRequirements.phone_prompt_for_phone)
    }
  },
  created () {
    _.extend(this, {
      email,
      required,
      DELIVERY_TYPE_OPTIONS
    })
  },
  methods: {
    handlePhoneChange (phoneObject) {
      if (phoneObject?.isValid?.()) {
        this.order.deliveryPhone = phoneObject.nationalNumber
        this.order.deliveryPhoneCountryCode = phoneObject.country
      } else {
        this.order.deliveryPhone = null
        this.order.deliveryPhoneCountryCode = null
      }
    },
    async validateDeliveryEmailAddress (val) {
      const hasValidDeliveryEmailAddress = email.$validator(val.toLowerCase()) ? val : null

      if (!hasValidDeliveryEmailAddress) {
        this.$emit('showMarketingEmailsOptIn', false)
        this.$emit('receiveMarketingEmails', false)
        return
      }

      const anonymousUser = new User({
        options: {
          fields: 'anonymous',
          queryName: 'findAnonymousUserByEmail',
          variables: {
            email: val
          }
        }
      })

      await anonymousUser.fetch()

      if (anonymousUser.isRemote) {
        this.$emit('showMarketingEmailsOptIn', true)
      }

      anonymousUser.destroy()
    },
    validate () {
      if (this.order.anonymous) {
        const emailValid = !this.collectEmail || !this.$refs.deliveryEmailAddress || this.$refs.deliveryEmailAddress.validate()
        const firstNameValid = !this.collectName || !this.$refs.deliveryFirstName || this.$refs.deliveryFirstName.validate()
        const lastNameValid = !this.collectName || !this.$refs.deliveryLastName || this.$refs.deliveryLastName.validate()
        const phoneValid = !this.collectPhone || !this.$refs.deliveryPhone || this.$refs.deliveryPhone.validate()

        if (!emailValid || !firstNameValid || !lastNameValid || !phoneValid) return false
      }

      return true
    }
  }
}
</script>
